import React from 'react';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureFlags, useSessionStorage } from 'hooks';
import classNames from 'classnames';
import { routes as ROUTES } from 'constants/config';
import * as actions from '../../../actions/journeyBuilder';
import { formattedDateTime } from '../__helpers__/journeyHelper';

const TopbarBuilder = () => {
  const { journeyStatus, steps, autosavedAt, updatedAt } = useSelector(
    state => state,
  );
  const { journeyId } = useParams();
  const { showBanner, disableFinishEditing } = useSelector(state => state.ui);
  const isIframedInSeismic = useSessionStorage('iframedInSeismic');
  const hasUnificationFeatureFlag = useFeatureFlags(
    'readiness-enable-unification',
  );
  const dispatch = useDispatch();

  const formatUpdatedAt = formattedDateTime(updatedAt);

  const timestamp = autosavedAt || formatUpdatedAt;

  const removeTempSteps = () => {
    steps.forEach((step, index) => {
      if (!step.stepId) {
        dispatch(
          actions.removeStep({
            index,
          }),
        );
      } else {
        dispatch(
          actions.updateStep({ index, step: { ...step, editing: false } }),
        );
      }
    });
  };

  const timestampTranslations = updatedTime => {
    const currentTime = formattedDateTime();
    const currentTimeDate = moment(currentTime, 'MMMM D, YYYY at h:mm A');
    const timeDate = moment(updatedTime, 'MMMM D, YYYY at h:mm A');
    const differenceInMinutes = currentTimeDate.diff(timeDate, 'minutes');

    switch (true) {
      case differenceInMinutes < 1:
        return t('journeys.builder.last_saved.seconds_ago');
      case differenceInMinutes >= 1 && differenceInMinutes < 60:
        return t('journeys.builder.last_saved.minutes_ago', {
          count: differenceInMinutes,
        });
      case differenceInMinutes >= 60 && differenceInMinutes < 1440:
        return t('journeys.builder.last_saved.hours_ago', {
          count: Math.floor(differenceInMinutes / 60),
        });
      default:
        return t('journeys.builder.last_saved.default', {
          at_time: updatedTime,
        });
    }
  };

  const STATES = {
    DRAFT: 'draft',
    IN_PROGRESS_CHANGES: 'in_progress_changes',
    PUBLISHED: 'published',
  };

  const translations = {
    [STATES.DRAFT]: t('journeys.state.draft'),
    [STATES.IN_PROGRESS_CHANGES]: t('journeys.state.in_progress_changes'),
    [STATES.PUBLISHED]: t('journeys.state.published'),
  };

  const indicatorClassName = classNames('Pill Pill--dot mr-8', {
    'Pill--dot--light': journeyStatus === 'published',
    'Pill--tealLighter': journeyStatus === 'draft',
    'Pill--outline Pill--light': journeyStatus === 'in_progress_changes',
  });

  const topbarClassName = classNames(
    'JourneyBuilderTopbar JourneyBuilderTopbar-editing',
    {
      'JourneyBuilderTopbar-editing--showBanner': showBanner,
      'JourneyBuilderTopbar--iFrame':
        isIframedInSeismic && hasUnificationFeatureFlag,
    },
  );
  const buttonClassName = classNames('JourneyBuilderTopbar-finishEditing', {
    'JourneyBuilderTopbar-finishEditing--disabled': disableFinishEditing,
  });

  return (
    <div className={topbarClassName}>
      <div className="JourneyBuilderTopbar-title">
        <h1>{t('journeys.builder.title')}</h1>
      </div>
      <div className="JourneyBuilderTopbar-actionButtons">
        {timestamp && (
          <div
            className="JourneyBuilderTopbar-autosaveTimestamp"
            data-testid="JourneyBuilderTopbar-autosaveTimestamp"
          >
            {timestampTranslations(timestamp)}
          </div>
        )}
        <div className="JourneyBuilderTopbar-horizontalDivider" />
        <div className="JourneyBuilderTopbar-publishStatus">
          <span className={indicatorClassName} />
          {translations[journeyStatus]}
        </div>
        <button
          disabled={disableFinishEditing}
          type="button"
          children={t('journeys.builder.finish_editing')}
          className={buttonClassName}
          onClick={() => {
            removeTempSteps();
            window.location.assign(ROUTES.JOURNEY.OVERVIEW(journeyId));
          }}
        />
      </div>
    </div>
  );
};

export default TopbarBuilder;
